import { Checkbox, cn } from "@au-schein-de/au-ui-library";
import type { ComponentProps } from "react";

type ClickableCheckboxRowProps = Omit<
	ComponentProps<typeof Checkbox>,
	"onCheckedChange"
> & {
	onRowClick: () => void;
	rowTestId?: string;
	rowClassName?: string;
	count?: number;
};

export function ClickableCheckboxRow({
	onRowClick,
	rowTestId,
	rowClassName,
	disabled,
	label,
	checked,
	count,
	...checkboxProps
}: ClickableCheckboxRowProps) {
	const handleClick = () => {
		if (!disabled && onRowClick) {
			onRowClick();
		}
	};

	const ariaLabel = typeof label === "string" ? label : undefined;
	const ariaChecked = checked === "indeterminate" ? "mixed" : Boolean(checked);

	return (
		<div
			className={cn(
				disabled ? "cursor-not-allowed" : "cursor-pointer",
				// When count is shown, make label take full width so count aligns to the end
				count !== undefined && "[&_label]:flex-1",
				rowClassName,
			)}
			onClick={handleClick}
			onKeyDown={(e) => {
				if (e.key === "Enter" || e.key === " ") {
					e.preventDefault();
					handleClick();
				}
			}}
			// biome-ignore lint/a11y/useSemanticElements: Custom checkbox wrapper for clickable row styling
			role="checkbox"
			aria-checked={ariaChecked}
			aria-label={ariaLabel}
			aria-disabled={disabled}
			tabIndex={disabled ? -1 : 0}
			data-testid={rowTestId}
		>
			<div className="pointer-events-none">
				<Checkbox
					disabled={disabled}
					checked={checked}
					label={
						count !== undefined ? (
							<span className="flex items-center justify-between w-full">
								<span>{label}</span>
								<span
									className="text-gray-500 text-body-sm"
									data-testid={rowTestId ? `${rowTestId}-count` : undefined}
								>
									{count}
								</span>
							</span>
						) : (
							label
						)
					}
					{...checkboxProps}
				/>
			</div>
		</div>
	);
}
